import Vue from 'vue';
import { Ref, computed, onBeforeUnmount, ref, watch } from '@vue/composition-api';
import orderBy from 'lodash/orderBy';
import * as storymapStoreModule from '../../store';
import { Subtask, Activity, Task, ViewType, ToolIntegrationToolType, SubtaskStatusCategory, SubtaskStatus, StorymapState } from '../types';
import { CardColor } from '@/api/workspaceSettings/queries';
import { subscriptionManager, FeatureAddOn } from '@/_core/utils/features';
import { currentUserWsRoles } from '@/_core/utils/roles';
import { confirm } from '@/_core/app-components/use';
import { cleanRestirctedMarkdown } from '@/_core/utils/misc';
import { getTaskRelease } from './useTaskRelease';

// Import for internal use
import { subtasksWithoutArchives } from './useFilterCommon';
import { getVisibleActivitiesForNav, getVisibleTasksForNav } from './useHideUnfiltered';

// Re-export filter computeds from useFilterCommon to maintain backward compatibility
export { isFiltered, filteredSubtasks, subtasksWithoutArchives } from './useFilterCommon';

export const getActiveCard = (): Activity | Task | Subtask => {
    const state = storymapStoreModule.useStorymapStore().state;
    const card = state.subtasks.find((x) => x.isActive) ?? state.tasks.find((x) => x.isActive) ?? state.activities.find((x) => x.isActive);

    return card;
};

export const moveSubtaskInActivity = (current: Subtask, nextTasks: Task[], state: StorymapState): boolean => {
    const navigationState = state.navigationState;

    // Get the original rectangle or store it if not already stored
    if (!navigationState.originalSubtask) {
        const currentElement = document.getElementById(`subtask-${current.id}`);
        if (!currentElement) return false; // Current subtask element not found

        navigationState.originalSubtask = current;
    }

    const originalElement = document.getElementById(`subtask-${navigationState.originalSubtask.id}`);
    if (!originalElement) return false; // Original subtask element not found
    const originalRect = originalElement.getBoundingClientRect();
    const originalTop = originalRect.top;
    const originalBottom = originalRect.bottom;
    const originalMiddleY = originalTop + originalRect.height / 2;

    for (const task of nextTasks) {
        let closestSubtask: Subtask | null = null;
        let maxVerticalOverlap = -1;
        let minVerticalDistance = Infinity;
        const subtasks = getTaskRelease(current.releaseId, task.id).value;

        for (const subtask of subtasks) {
            const element = document.getElementById(`subtask-${subtask.id}`);
            if (!element) continue; // Skip if element not found

            const rect = element.getBoundingClientRect();

            const targetTop = rect.top;
            const targetBottom = rect.bottom;
            const targetMiddleY = targetTop + rect.height / 2;

            // Calculate vertical overlap with originalRect
            const overlapTop = Math.max(originalTop, targetTop);
            const overlapBottom = Math.min(originalBottom, targetBottom);
            const verticalOverlap = overlapBottom - overlapTop;

            if (verticalOverlap > 0) {
                if (verticalOverlap > maxVerticalOverlap) {
                    // Found a subtask with greater vertical overlap
                    maxVerticalOverlap = verticalOverlap;
                    minVerticalDistance = Math.abs(originalMiddleY - targetMiddleY);
                    closestSubtask = subtask;
                } else if (verticalOverlap === maxVerticalOverlap) {
                    // If overlap is equal, choose the closer one vertically
                    const verticalDistance = Math.abs(originalMiddleY - targetMiddleY);
                    if (verticalDistance < minVerticalDistance) {
                        minVerticalDistance = verticalDistance;
                        closestSubtask = subtask;
                    }
                }
            } else if (maxVerticalOverlap === -1) {
                // No overlapping subtasks found yet; consider vertical distance
                const verticalDistance = Math.abs(originalMiddleY - targetMiddleY);
                if (verticalDistance < minVerticalDistance) {
                    minVerticalDistance = verticalDistance;
                    closestSubtask = subtask;
                }
            }
        }

        if (closestSubtask) {
            closestSubtask.isActive = true;
            current.isActive = false;

            // Keep originalRect for subsequent moves
            return true;
        }
    }

    return false;
};

export const getCardById = (id: string): Activity | Task | Subtask => {
    const state = storymapStoreModule.useStorymapStore().state;
    const card =
        state.subtasks.find((x) => x.id === id) ?? state.tasks.find((x) => x.id === id) ?? state.activities.find((x) => x.id === id);

    return card;
};

export const allCards = computed(() => {
    const state = storymapStoreModule.useStorymapStore().state;
    return [...state.activities, ...state.tasks, ...state.subtasks];
});

export const allCardsWithoutArchives = computed(() => {
    const state = storymapStoreModule.useStorymapStore().state;
    return [...state.activities, ...state.tasks, ...subtasksWithoutArchives.value];
});

export enum CardType {
    Activity,
    Task,
    Subtask,
}

export const getCardType = (card: Activity | Task | Subtask): CardType => {
    if (isSubtask(card)) {
        return CardType.Subtask;
    }

    if (isTask(card)) {
        return CardType.Task;
    }

    return CardType.Activity;
};

export const isSubtask = (card: Activity | Task | Subtask): card is Subtask => {
    if (!card) {
        return false;
    }
    return (card as Subtask).activityId !== undefined && (card as Subtask).taskId !== undefined;
};

export const isTask = (card: Activity | Task | Subtask): card is Task => {
    if (!card) {
        return false;
    }
    return (card as Subtask).activityId !== undefined && (card as Subtask).taskId === undefined;
};

export const isActivity = (card: Activity | Task | Subtask): card is Activity => {
    if (!card) {
        return false;
    }
    return (card as Subtask).activityId === undefined && (card as Subtask).taskId === undefined;
};

export const GetNewEmptyActivity = (defaults: Partial<Activity>): Activity => {
    return {
        id: null,
        title: '',
        description: '',
        color: 0,
        order: 0,
        assigneeUserId: null,
        key: null,
        isCollapsed: false,

        isNew: true,
        isActive: false,
        activeUsers: [],
        isMultiselected: false,
        isTitleEditing: false,
        isCopied: false,
        isCut: false,
        personaIds: [],
        annotationIds: [],

        hasDescription: false,
        hasAttachment: false,
        hasComment: false,
        hasFeedback: false,
        isSaving: false,

        isLinkedToIntegrationTool: false,
        linkedUrl: '',
        linkedItemKey: '',
        linkedItemTypeId: null,
        linkedItemTypeName: null,

        createdAt: new Date(),
        createdByUserId: null,
        lastUpdatedAt: null,
        lastUpdatedByUserId: null,

        ...defaults,
    };
};

export const GetNewEmptyTask = (defaults: Partial<Task>): Task => {
    return {
        id: null,
        activityId: null,
        title: '',
        description: '',
        color: 0,
        order: 0,
        assigneeUserId: null,
        key: null,

        isNew: true,
        isActive: false,
        activeUsers: [],
        isMultiselected: false,
        isTitleEditing: false,
        isCopied: false,
        isCut: false,
        personaIds: [],
        annotationIds: [],

        hasDescription: false,
        hasAttachment: false,
        hasComment: false,
        hasFeedback: false,
        isSaving: false,

        isLinkedToIntegrationTool: false,
        linkedUrl: '',
        linkedItemKey: '',
        linkedItemTypeId: null,
        linkedItemTypeName: null,

        createdAt: new Date(),
        createdByUserId: null,
        lastUpdatedAt: null,
        lastUpdatedByUserId: null,

        ...defaults,
    };
};

export const GetNewEmptySubtask = (defaults: Partial<Subtask>): Subtask => {
    return {
        id: null,
        activityId: null,
        taskId: null,
        releaseId: null,
        title: '',
        description: '',
        color: 0,
        status: null,
        order: 0,
        assigneeUserId: null,
        key: null,

        isNew: true,
        isActive: false,
        activeUsers: [],
        isMultiselected: false,
        isTitleEditing: false,
        isCopied: false,
        isCut: false,
        personaIds: [],
        annotationIds: [],
        basicPrioValue: null,
        businessValue: null,
        effort: null,
        kanoPrioValue: null,
        moscowPrioValue: null,
        releaseViewOrder: null,

        hasDescription: false,
        hasAttachment: false,
        hasComment: false,
        hasFeedback: false,
        isSaving: false,

        isLinkedToIntegrationTool: false,
        linkedUrl: '',
        linkedItemKey: '',
        linkedItemTypeId: null,
        linkedItemTypeName: null,
        estimation: null,

        createdAt: new Date(),
        createdByUserId: null,
        lastUpdatedAt: null,
        lastUpdatedByUserId: null,

        ...defaults,
    };
};

export const deleteCard = (id: string) => {
    const card = getCardById(id);
    if (!card) {
        return;
    }

    const state = storymapStoreModule.useStorymapStore().state;

    if (isActivity(card)) {
        state.activities.filter((x) => x.order > card.order).forEach((x) => x.order--);
        state.activities = state.activities.filter((x) => x.id !== card.id);

        state.tasks = state.tasks.filter((x) => x.activityId !== card.id);
        state.subtasks = state.subtasks.filter((x) => x.activityId !== card.id);
    }

    if (isTask(card)) {
        state.tasks.filter((x) => x.activityId == card.activityId && x.order > card.order).forEach((x) => x.order--);
        state.tasks = state.tasks.filter((x) => x.id !== card.id);

        state.subtasks = state.subtasks.filter((x) => x.taskId !== card.id);
    }

    if (isSubtask(card)) {
        state.subtasks
            .filter((x) => x.taskId == card.taskId && x.releaseId == card.releaseId && x.order > card.order)
            .forEach((x) => x.order--);
        state.subtasks = state.subtasks.filter((x) => x.id !== card.id);
    }
};

// Counts the cards that deleteCard removes together with the card, from the same raw lists, so cards in archived
// releases, annotation cards and cards hidden by a filter are included. Unsaved new cards are left out.
export const getDeleteCardConfirmationMessage = (card: Activity | Task | Subtask, title: string = card.title): string => {
    const state = storymapStoreModule.useStorymapStore().state;

    let numberOfChildren = 0;

    if (isActivity(card)) {
        numberOfChildren =
            state.tasks.filter((x) => x.activityId === card.id && !x.isNew).length +
            state.subtasks.filter((x) => x.activityId === card.id && !x.isNew).length;
    }

    if (isTask(card)) {
        numberOfChildren = state.subtasks.filter((x) => x.taskId === card.id && !x.isNew).length;
    }

    const cardText = `the card "${cleanRestirctedMarkdown(title)}"`;

    if (numberOfChildren === 1) {
        return `Are you sure you want to delete ${cardText} and its child?`;
    }

    if (numberOfChildren > 1) {
        return `Are you sure you want to delete ${cardText} and its ${numberOfChildren} children?`;
    }

    return `Are you sure you want to delete ${cardText}?`;
};

export const getNearestCardIfPossibleToSubtask = (cardId: string) => {
    const card = getCardById(cardId);
    if (!card) {
        return;
    }

    if (!isSubtask(card)) {
        return;
    }

    const state = storymapStoreModule.useStorymapStore().state;

    const cardTaskOrder = state.tasks.find((x) => x.id === card.taskId)?.order;
    const cardActivityOrder = state.activities.find((x) => x.id === card.activityId)?.order;

    const subtasks = orderBy(
        state.subtasks
            .filter((x) => x.releaseId === card.releaseId && x.id !== card.id)
            .map((x) => {
                const task = state.tasks.find((t) => t.id === x.taskId);
                const activity = state.activities.find((a) => a.id === x.activityId);

                return {
                    ...x,
                    taskOrder: task.order,
                    taskDistance: Math.abs(cardTaskOrder - task.order),
                    activityDistance: Math.abs(cardActivityOrder - activity.order),
                };
            }),
        ['activityDistance', 'taskDistance', 'taskOrder', 'order'],
        ['asc', 'asc', 'asc', 'asc']
    );

    const subtasksDescOrders = orderBy(subtasks, ['taskOrder', 'order'], ['asc', 'desc']);

    const next = subtasks.filter((x) => x.taskId === card.taskId && x.order > card.order);

    if (next.length > 0) {
        return next[0];
    }

    const prev = subtasksDescOrders.filter((x) => x.taskId === card.taskId && x.order < card.order);

    if (prev.length > 0) {
        return prev[0];
    }

    const flatTasks = orderBy(
        state.tasks.map((task) => {
            const activity = state.activities.find((a) => a.id === task.activityId);
            return { task, activityOrder: activity?.order ?? 0, taskOrder: task.order };
        }),
        ['activityOrder', 'taskOrder'],
        ['asc', 'asc']
    ).map((x) => x.task);

    const currentTaskIdx = flatTasks.findIndex((t) => t.id === card.taskId);

    const pickFirstSubtaskOfTask = (taskId: string): Subtask | null => {
        const candidates = state.subtasks.filter(
            (s) => s.taskId === taskId && s.releaseId === card.releaseId && s.id !== card.id
        );
        return orderBy(candidates, 'order', 'asc').shift() ?? null;
    };

    for (let i = currentTaskIdx + 1; i < flatTasks.length; i++) {
        const hit = pickFirstSubtaskOfTask(flatTasks[i].id);
        if (hit) {
            return hit;
        }
    }

    for (let i = currentTaskIdx - 1; i >= 0; i--) {
        const hit = pickFirstSubtaskOfTask(flatTasks[i].id);
        if (hit) {
            return hit;
        }
    }

    return null;
};

export const getNearestActivity = (cardId: string): Activity | null => {
    const card = getCardById(cardId);
    if (!card || !isActivity(card)) {
        return null;
    }

    const state = storymapStoreModule.useStorymapStore().state;
    const activities = getVisibleActivitiesForNav(state).filter((x) => x.id !== card.id);

    const next = orderBy(activities.filter((x) => x.order > card.order), 'order', 'asc').shift();
    if (next) {
        return next;
    }

    const prev = orderBy(activities.filter((x) => x.order < card.order), 'order', 'desc').shift();
    return prev ?? null;
};

export const getNearestTask = (cardId: string): Task | Activity | null => {
    const card = getCardById(cardId);
    if (!card || !isTask(card)) {
        return null;
    }

    const state = storymapStoreModule.useStorymapStore().state;
    const activities = getVisibleActivitiesForNav(state);
    const tasks = getVisibleTasksForNav(state).filter((x) => x.id !== card.id);

    const siblingNext = orderBy(
        tasks.filter((x) => x.activityId === card.activityId && x.order > card.order),
        'order',
        'asc'
    ).shift();
    if (siblingNext) {
        return siblingNext;
    }

    const siblingPrev = orderBy(
        tasks.filter((x) => x.activityId === card.activityId && x.order < card.order),
        'order',
        'desc'
    ).shift();
    if (siblingPrev) {
        return siblingPrev;
    }

    const currentActivity = activities.find((x) => x.id === card.activityId);
    if (!currentActivity) {
        return null;
    }

    const nextActivity = orderBy(
        activities.filter((x) => x.order > currentActivity.order),
        'order',
        'asc'
    ).shift();
    if (nextActivity) {
        const firstTask = orderBy(tasks.filter((x) => x.activityId === nextActivity.id), 'order', 'asc').shift();
        if (firstTask) {
            return firstTask;
        }
    }

    const prevActivity = orderBy(
        activities.filter((x) => x.order < currentActivity.order),
        'order',
        'desc'
    ).shift();
    if (prevActivity) {
        const firstTask = orderBy(tasks.filter((x) => x.activityId === prevActivity.id), 'order', 'asc').shift();
        if (firstTask) {
            return firstTask;
        }
    }

    return currentActivity;
};

export const getNearestCardAfterDelete = (cardId: string): Activity | Task | Subtask | null => {
    const card = getCardById(cardId);
    if (!card) {
        return null;
    }

    if (isSubtask(card)) {
        return getNearestCardIfPossibleToSubtask(cardId) ?? null;
    }

    if (isTask(card)) {
        return getNearestTask(cardId);
    }

    return getNearestActivity(cardId);
};

export const isDraggingCard = computed(() => storymapStoreModule.useStorymapStore().state.isDraggingCard);
export const zoomLevel = computed(() => storymapStoreModule.useStorymapStore().state.zoomLevel);
export const isAiDataEmpty = computed(() => {
    const aiData = storymapStoreModule.useStorymapStore().state.aiData;

    return !aiData || !aiData.typeOfProduct?.trim() || !aiData.purpose?.trim() || !aiData.keyFeatures?.trim();
});

export const canUseAi = computed(() => subscriptionManager.hasFeatureAddOn(FeatureAddOn.AI) && !isAiDataEmpty.value);

export const tryTurnOnAi = () => {
    if (!currentUserWsRoles.isSubscriptionAdmin && !subscriptionManager.hasFeatureAddOn(FeatureAddOn.AI)) {
        confirm.show({
            title: 'AI feature is turned off on this workspace',
            message: 'Please contact your subscription admin to enable StoriesOnBoard AI Assistant.',
            acceptBtnText: 'Ok',
            isHideCancelBtn: true,
        });
        return;
    }

    const store = storymapStoreModule.useStorymapStore();
    store.dispatch(new storymapStoreModule.SetAiDialogIsOpen(true));
};

export const ensureAiDataThenCall = (callback: () => void) => {
    const hasAiFeatureAddon = subscriptionManager.hasFeatureAddOn(FeatureAddOn.AI);

    if (!canUseAi.value) {
        if (!currentUserWsRoles.isSubscriptionAdmin && !hasAiFeatureAddon) {
            confirm.show({
                title: 'AI feature is turned off on this workspace',
                message: 'Please contact your subscription admin to enable StoriesOnBoard AI Assistant.',
                acceptBtnText: 'Ok',
                isHideCancelBtn: true,
            });
            return;
        }

        const store = storymapStoreModule.useStorymapStore();
        store.dispatch(
            new storymapStoreModule.SetAiDialogIsOpen(true, {
                onDialogClose: callback,
            })
        );
        return;
    } else {
        callback();
    }
};

export const ensureAiDataReady = (): Promise<boolean> => {
    if (canUseAi.value === true) {
        return Promise.resolve(true);
    }

    const hasAiFeatureAddon = subscriptionManager.hasFeatureAddOn(FeatureAddOn.AI);
    if (!currentUserWsRoles.isSubscriptionAdmin && !hasAiFeatureAddon) {
        confirm.show({
            title: 'AI feature is turned off on this workspace',
            message: 'Please contact your subscription admin to enable StoriesOnBoard AI Assistant.',
            acceptBtnText: 'Ok',
            isHideCancelBtn: true,
        });
        return Promise.resolve(false);
    }

    const store = storymapStoreModule.useStorymapStore();

    if (store.state.aiDataDialogState.isAiDataDialogOpen) {
        return Promise.resolve(false);
    }

    store.dispatch(new storymapStoreModule.SetAiDialogIsOpen(true));

    return new Promise<boolean>((resolve) => {
        const unwatch = watch(
            () => store.state.aiDataDialogState.isAiDataDialogOpen,
            (isOpen) => {
                if (!isOpen) {
                    unwatch();
                    resolve(!isAiDataEmpty.value);
                }
            },
        );
    });
};

//const state = null;

export const currentViewOptions = computed(() => {
    const state = storymapStoreModule.useStorymapStore().state;

    if (!state.isInitialized) {
        return null;
    }

    if (state.currentViewType == ViewType.Discovery) {
        return state.discoveryViewOptions;
    }

    if (state.currentViewType == ViewType.Planning) {
        return state.planningViewOptions;
    }

    if (state.currentViewType == ViewType.Backlog) {
        return state.backlogViewOptions;
    }

    if (state.currentViewType == ViewType.Tracking) {
        return state.trackingViewOptions;
    }

    return null;
});

export const numberOfOptionalBlocksOnCard = computed(() => {
    if (currentViewOptions.value.isShowPersonaBlock && currentViewOptions.value.isShowAnnotation) {
        return 2;
    } else if (!currentViewOptions.value.isShowPersonaBlock && !currentViewOptions.value.isShowAnnotation) {
        return 0;
    } else {
        return 1;
    }
});

export const canEdit = computed(() => storymapStoreModule.useStorymapStore().state.canEdit);

export const canUserPush = computed(() => {
    const store = storymapStoreModule.useStorymapStore();
    return store.state.isIntegrationEnabled && !store.state.isMissingImpersonationToken && store.state.canEdit;
});

const isEditingAnyCardTitleValue = ref(false);
let isEditingAnyCardTitleWatched = false;

export const isEditingAnyCardTitle = computed(() => {
    if (!isEditingAnyCardTitleWatched) {
        isEditingAnyCardTitleWatched = true;
        // On its own Vue instance: a composition API watch made here would belong to the component rendering right now.
        new Vue().$watch(
            () => {
                const state = storymapStoreModule.useStorymapStore().state;

                return [...state.activities, ...state.tasks, ...state.subtasks].some((x) => x.isNew || x.isTitleEditing);
            },
            (value: boolean) => {
                isEditingAnyCardTitleValue.value = value;
            },
            { immediate: true, sync: true } as any
        );
    }

    return isEditingAnyCardTitleValue.value;
});

export const integrationToolName = computed(() => {
    const state = storymapStoreModule.useStorymapStore().state;
    return getIntegrationToolName(state.integrationToolType);
});
export const integrationToolIcon = computed(() => {
    const state = storymapStoreModule.useStorymapStore().state;
    return getIntegrationToolIcon(state.integrationToolType);
});

export const getIntegrationToolName = (toolType: ToolIntegrationToolType) => {
    switch (toolType) {
        case ToolIntegrationToolType.GitHub:
            return 'GitHub';
        case ToolIntegrationToolType.Trello:
            return 'Trello';
        case ToolIntegrationToolType.PivotalTracker:
            return 'Pivotal Tracker';
        case ToolIntegrationToolType.Jira:
            return 'Jira';
        case ToolIntegrationToolType.Tfs:
            return 'TFS';
        case ToolIntegrationToolType.Vsts:
            return 'Azure DevOps';
        default:
            return null;
    }
};

export const getIntegrationToolIcon = (toolType: ToolIntegrationToolType) => {
    switch (toolType) {
        case ToolIntegrationToolType.GitHub:
            return '$fabGithub';
        case ToolIntegrationToolType.Trello:
            return '$fabTrello';
        case ToolIntegrationToolType.PivotalTracker:
            return '$fasPlug';
        case ToolIntegrationToolType.Jira:
            return '$fabJira';
        case ToolIntegrationToolType.Tfs:
        case ToolIntegrationToolType.Vsts:
            return '$fabMicrosoft';
        default:
            return null;
    }
};

// The panel that set the board's padding last. As before, the last panel to open or close decides it.
let boardPaddingOwner: object = null;

export const useSetBoardPadding = (isOpen: Ref<boolean>, padding: number) => {
    const owner = {};
    // A variable, because release bodies come and go while a panel is open. It is set on the page's root, which outlives the panel.
    const setPadding = (value: string) => document.getElementById('sm')?.style.setProperty('--board-panel-padding', value);
    watch(isOpen, (isOpen) => {
        boardPaddingOwner = owner;
        setPadding(isOpen ? `${padding}px` : '0');
    });
    // A panel that goes away while open takes its padding with it.
    onBeforeUnmount(() => {
        if (boardPaddingOwner === owner) {
            boardPaddingOwner = null;
            setPadding('0');
        }
    });
};

export const getDefaultStatus: () => SubtaskStatus = () => {
    const state = storymapStoreModule.useStorymapStore().state;
    return orderBy(
        state.subtaskStatuses.filter((x) => x.category === SubtaskStatusCategory.Todo),
        (x) => x.order
    )[0];
};

export const getDefaultDoneStatus: () => SubtaskStatus = () => {
    const state = storymapStoreModule.useStorymapStore().state;
    return orderBy(
        state.subtaskStatuses.filter((x) => x.category === SubtaskStatusCategory.Done),
        (x) => x.order
    )[0];
};

export const unsupportedIntegrationTools = [
    ToolIntegrationToolType.PivotalTracker,
    ToolIntegrationToolType.Vsts,
    ToolIntegrationToolType.Tfs,
];

// Re-export hide unfiltered computeds
export {
    isHideUnfilteredActive,
    visibleTasks,
    visibleActivities,
    hideUnfilteredMatchingSubtasks,
    matchingSubtaskIdSet,
    visibleTaskIdSet,
    visibleActivityIdSet,
    getVisibleActivitiesForNav,
    getVisibleTasksForNav,
} from './useHideUnfiltered';

export { createStorymapCardAiProvider } from './useStorymapCardAiProvider';
export {
    getFirstTrackingVisibleCard,
    getTrackingVisibleCardIds,
    getTrackingVisibleCardPosition,
    getTrackingVisibleCards,
    getTrackingVisibleStatusColumns,
    getTrackingVisibleStatuses,
    sortTrackingCardsInVisibleOrder,
    trackingVisibleCardIds,
    trackingVisibleStatusColumns,
} from './useTrackingVisibleCards';
